import { Box, Portal } from '@mui/material';
import Lottie from 'lottie-react';
import { Howl } from 'howler';
import { useEffect, useRef } from 'react';
import { useGetLottieAnimationData, useInitReinforcer, usePreloadReinforcer } from './ReinforcerV2Hooks';
import ObjectUtils from '../../../utils/ObjectUtils';
import Logger from '../../../utils/Logger';
import LoadingPlaceHolder from '../LoadingPlaceHolder';
import { filterUndefined } from '../../../utils/FilterUndefinedObject';

const defaultProps = {
  reinforcerList: [],
  enableAudio: false,
};

export default function ReinforcerV2(props) {
  const mergedProps = { ...defaultProps, ...filterUndefined(props) };
  const { reinforcerList, enableAudio } = mergedProps;
  usePreloadReinforcer({ reinforcerList, enableAudio });
  const { reinforceState, setReinforceState } = useInitReinforcer();
  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(
    () => () => {
      clearInterval(intervalRef.current);
      clearTimeout(timeoutRef.current);
    },
    []);

  const {
    animationData,
    setAnimationData,
    loading,
    getLottieAnimationData,
  } = useGetLottieAnimationData();

  const reinforcerData = reinforcerList.filter((r) => r).find((r) => r.id === reinforceState.reinforceId) || {};
  const { manifest } = reinforcerData || {};
  const sound = (manifest || []).find((m) => m.id === 'sound');
  const spritesheetData = (manifest || []).find((m) => m.id === 'spritesheetData');

  useEffect(() => {
    if (spritesheetData) {
      getLottieAnimationData(spritesheetData.src);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spritesheetData]);

  if (!spritesheetData) {
    return null;
  }

  if (!reinforceState.open || !reinforcerList || reinforcerList.length === 0) {
    return null;
  }

  const { promise } = reinforceState;
  const handleOnFinish = () => {
    setReinforceState({
      open: false,
      reinforceId: '',
      promise: null,
    });
    setAnimationData(null);
    clearTimeout(timeoutRef.current);
    clearInterval(intervalRef.current);
    timeoutRef.current = null;
    ObjectUtils.setTimeout(() => {
      if (promise && promise.resolve) {
        promise.resolve();
      }
    });
  };

  const handleReject = (logMessage) => {
    Logger.logWarn(logMessage);
    handleOnFinish();
  };

  if (!animationData) {
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      if (!loading && animationData) {
        clearInterval(intervalRef.current);
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      // if the resurces cannot be loaded within 10 seconds, then skip the reinforcer
      if (!timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
          clearInterval(intervalRef.current);
          if (!reinforceState.ready) {
            Logger.logError(`Unable to load reinforcer ${reinforceState.reinforceId}`);
            handleReject(`Assets for ${reinforceState.reinforceId} either are not ready or cannot be loaded.`);
          }
        }, 10000);
      }
    }, 500);
  }

  if (loading) {
    return (
      <Portal container={document.body}>
        <Box
          position='fixed'
          zIndex={10000}
          top={0}
          left={0}
          display='flex'
          justifyContent='center'
          alignItems='center'
          width='100%'
          height='100%'
          data-test='reinforcer-player'
        >
          <Box className='reinforcer-container' sx={{ backgroundColor: 'white', padding: '20px', border: '1px solid #E0E0E0', borderRadius: '10px' }}>
            <div className='loading'>
              <LoadingPlaceHolder message='Reinforcer is loading.' />
            </div>
          </Box>
        </Box>
      </Portal>
    );
  }

  if (!animationData) {
    return null;
  }

  return (
    <Portal container={document.body}>
      <Box position='fixed' top={0} left={0} zIndex={10000}>
        <Lottie
          animationData={animationData}
          style={{ width: '100vw', height: '100vh' }}
          loop={false}
          onDOMLoaded={() => {
            if (enableAudio && sound && sound.src) {
              const soundObject = new Howl({
                src: sound.src,
              });
              soundObject.play();
            }
          }}
          onComplete={() => {
            handleOnFinish();
          }}
        />
      </Box>
    </Portal>
  );
}
